import { useCallback, useEffect, useState } from 'react';
import { puertoDeDatos } from './elPuertoDeDatos';
import type { CifrasDelPanel } from './tipos';

/**
 * Las cifras del panel, con sus estados.
 *
 * Los cuatro estados de una vista con datos, que hasta ahora se contestaban en
 * el tipo porque no había petición que pudiera tardar ni fallar:
 *
 * | Estado | Aquí |
 * |---|---|
 * | Carga | `cargando`, y **empieza en `true`** |
 * | Vacío | lo pinta la pantalla: `cobertura === null`, `hayDatos === false` |
 * | Error | `error`, con el código que lanzó el puerto |
 * | Sin conexión | es el código `red`, que ya distingue el adaptador |
 *
 * ## La espera que hay que explicar
 *
 * La Aurora de identity se pausa sola y tardó **74 s** en despertar en dev el
 * 2026-09-15. La primera petición del día la paga, así que el estado de carga
 * de esta pantalla no es un parpadeo de 200 ms: puede ser un minuto largo, y la
 * pantalla tiene que decirlo en vez de parecer colgada.
 *
 * ## Por qué no se cancela al desmontar
 *
 * Porque no hace falta: lo único que hace el `then` es un `setState` sobre un
 * componente que puede haberse ido, y React ya ignora eso sin avisar desde la
 * 18. Meter un `AbortController` aquí sería código que sólo sirve para que
 * alguien lo lea y se pregunte qué evita.
 */
export function useCifras() {
  const [cifras, setCifras] = useState<CifrasDelPanel | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    puertoDeDatos
      .cifras()
      .then((d) => setCifras(d))
      .catch((e) => setError(e))
      .finally(() => setCargando(false));
  }, []);

  useEffect(cargar, [cargar]);

  return { cifras, cargando, error, reintentar: cargar };
}
